import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const PILLARS = [
  {
    num: "01",
    title: "Childcare Centres — Build & Lease",
    copy: "High-yield, turnkey childcare assets anchored by long-term commercial leases in NSW's fastest-growing family corridors.",
    color: "#FF1744",
    img: "/projects/childcare/cc-01.jpg",
    alt: "JAI HOMES childcare centre under construction — precision LGS steel frame elevation",
    span: "md:col-span-7",
    testId: "pillar-childcare",
  },
  {
    num: "02",
    title: "Gated Masterplanned Communities",
    copy: "Secure, community-driven residential estates with parks, playgrounds and modern amenities — where neighbours become mates.",
    color: "#FFC107",
    img: "/projects/buchanan/74-01.jpg",
    alt: "Completed JAI HOMES family home streetscape in a Buchanan master-planned estate",
    span: "md:col-span-5",
    testId: "pillar-communities",
  },
  {
    num: "03",
    title: "Bespoke Luxury Villas",
    copy: "Custom high-end homes, duplexes and knockdown-rebuild solutions crafted around your block, your light, your life.",
    color: "#D500F9",
    img: "/projects/duplex-beresfield/dx-01.jpg",
    alt: "Completed modern double-storey duplex by JAI HOMES — 23A Fletcher St, Beresfield NSW",
    badge: "Multi-Dwelling & High-Yield Residential",
    span: "md:col-span-5",
    testId: "pillar-villas",
  },
  {
    num: "04",
    title: "Commercial Complexes & Retail Hubs",
    copy: "Mixed-use developments, medical suites and neighbourhood retail hubs engineered for footfall and long-term tenancy.",
    color: "#00B0FF",
    img: "/images/projects/card-fitouts.jpg",
    alt: "Monstone commercial hospitality fit-out completed by JAI HOMES",
    span: "md:col-span-7",
    testId: "pillar-commercial",
  },
  {
    num: "05",
    title: "Requirement-Based Custom Builds",
    copy: "Own the site? We deliver tailored commercial and residential builds on client-owned land — your brief, our blueprint.",
    color: "#00E676",
    img: "/projects/residential/rh-05.jpg",
    alt: "Active JAI HOMES custom build site — waffle pod slab and reinforcement laid on a client-owned NSW block",
    span: "md:col-span-12",
    wide: true,
    testId: "pillar-custom",
  },
];

export const Pillars = () => (
  <section id="pillars" className="px-6 sm:px-12 lg:px-20 py-28 lg:py-36" data-testid="pillars-section">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-16 lg:mb-20"
      >
        <p className="font-display text-[11px] font-bold tracking-[0.35em] text-[#FF1744] mb-4">
          CHAPTER 01 — WHAT WE BUILD
        </p>
        <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.02] max-w-3xl">
          Five pillars. One signature:{" "}
          <em className="font-serif-lux italic font-semibold text-gradient-fire">built to last.</em>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {PILLARS.map((p, i) => (
          <motion.article
            key={p.num}
            data-testid={p.testId}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: (i % 2) * 0.12 }}
            className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] ${p.span}`}
            style={{ "--pillar": p.color }}
          >
            <div className={`relative overflow-hidden ${p.wide ? "h-64 md:h-80" : "h-64"}`}>
              <img
                src={p.img}
                alt={p.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] via-[#0A0E17]/35 to-transparent" />
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: `radial-gradient(70% 90% at 50% 100%, ${p.color}26, transparent 70%)` }}
              />
              <span
                className="absolute top-5 left-6 font-serif-lux italic text-6xl leading-none"
                style={{ color: p.color, textShadow: `0 0 30px ${p.color}80` }}
              >
                {p.num}
              </span>
              {p.badge && (
                <span
                  className="absolute bottom-4 left-5 rounded-full glass border border-white/15 px-3.5 py-1.5 font-display text-[10px] font-bold tracking-wide text-white/85 pointer-events-none"
                  data-testid={`${p.testId}-badge`}
                >
                  {p.badge}
                </span>
              )}
            </div>

            <div
              className="relative p-7 border-t-2 transition-shadow duration-500"
              style={{ borderTopColor: p.color }}
            >
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-display font-bold text-xl sm:text-2xl text-white tracking-tight">
                  {p.title}
                </h3>
                <ArrowUpRight
                  size={22}
                  className="shrink-0 mt-1 text-white/30 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  style={{ color: undefined }}
                />
              </div>
              <p className="mt-3 text-sm text-white/60 leading-relaxed max-w-xl">{p.copy}</p>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);
